/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { Box, Chip, FormControl, InputAdornment, MenuItem, MenuList, Select, Tab, Tabs } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { deleteResourceIds, saveResourceIds } from "@Actions/resource";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR } from "@Services/constants";
import { ObjectId } from "bson";
import Resource from "./Resource";
import Dependency from "./Dependency";
import ActivityFiles, { TaskFileRow } from "./ActivityFiles";
import { untranslated, useTranslation } from "@i18n";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import MilestoneIcon from "@Assets/icons/svg/Milestones.svg?react";
import TaskIcon from "@Assets/icons/svg/tasks.svg?react";
import ProjectIcon from "@Assets/images/project.svg?react";
import Text from "@CubeComponents/html/Text/Text";
import DownArrowIcon from "@Assets/icons/svg/arrowDown.svg?react";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import PopperComponent from "@CubeComponents/html/PopperComponent";
import IconComponent from "@CubeComponents/icons";
import { Task } from "./dhtmlxgantt";
import { ActivityInformationModalProps, type SelectedGanttTask } from "../interface";
import useGanttHelper, { toAvailability } from "./useGanttHelper";
import {
  DependencyAddEditData,
  Worktime,
  TaskLink,
  DeletedResource,
  ResourceAddEditData,
} from "@WebTypes/scheduleDomain";
import { RootState } from "src/reducers";
import type { GetTaskTypesResponse } from "@ServerTypes/tasks/tasks.schema";
import { objectIdString } from "@Helpers/schemas/common.schema";

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * Reads the server's `errorMessage` off an axios rejection. `catch` gives `unknown`, so the shape is
 * checked at each hop rather than asserted — a rejection that is not an axios one (a TypeError from
 * the gantt calls above, say) simply has no message and falls back to the generic toast.
 */
const readApiErrorMessage = (error: unknown): string | undefined => {
  if (!isRecord(error) || !isRecord(error.response) || !isRecord(error.response.data)) return undefined;
  const errorMessage = error.response.data.errorMessage;
  return typeof errorMessage === "string" ? errorMessage : undefined;
};

/** The activity date fields {@link getDateComponent} is rendered for. */
type ActivityDateField = "start_date" | "end_date" | "plannedStartDate" | "plannedEndDate";

/** `getTaskTypes` returns raw `TaskTypes` documents, so every field can be absent on the wire. */
type TaskTypeRow = NonNullable<GetTaskTypesResponse["result"]>[number];

function ActivityInformationModal(props: Readonly<ActivityInformationModalProps>) {
  const { t, i18n } = useTranslation();
  const {
    showActivityInformationModal,
    setShowActivityInformationModal,
    currentSelectedTask,
    setCurrentSelectedTask,
    ganttConfig,
    gantt,
    taskCounter,
    scheduleId,
    loadingActivity,
    setLoadingActivity,
  } = props;
  const { getHoursPerDay } = useGanttHelper();
  const [currentTab, setCurrentTab] = useState("General");
  const [deletedResources, setDeletedResources] = useState<DeletedResource[]>([]);
  const [resourceAddEditData, setResourceAddEditData] = useState<ResourceAddEditData[]>([]);
  const [deletedLinks, setDeletedLinks] = useState<TaskLink[]>([]);
  const [dependencyAddEditData, setDependencyAddEditData] = useState<DependencyAddEditData[]>([]);
  const [fileAddEditData, setFileAddEditData] = useState<TaskFileRow[]>([]);
  const [filesChanged, setFilesChanged] = useState(false);
  const [taskTypes, setTaskTypes] = useState<TaskTypeRow[]>([]);
  const [disableSave, setDisableSave] = useState(false);
  const [showCustomCalendars, setShowCustomCalendars] = useState(false);
  const [selectedCalendarName, setSelectedCalendarName] = useState<string>("");
  const [isCreatingActivity, setIsCreatingActivity] = useState<boolean>(false);
  const maxCharacterLength = 1000;
  const calendarItemRef = useRef<HTMLElement | null>(null);

  const dispatch = useDispatch();
  const GanttDetails = useSelector((state: RootState) => state.GanttDetails);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const currencySymbol = useSelector((state: RootState) => state.currencyDetails.symbolDetail?.name) ?? "";
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.tz.setDefault(userDetails.timeZone);

  useEffect(() => {
    if (showActivityInformationModal) {
      setFilesChanged(false);
      const selectedId = gantt.getSelectedId();
      const selectedTask = gantt.getTask(selectedId);
      if (selectedId) {
        setFileAddEditData(
          (selectedTask.taskFiles ?? []).map(
            (file: {
              fileVersionId: string | ObjectId;
              name?: string;
              weightage?: number | null;
              version?: number;
            }) => ({
              id: uuidv4(),
              fileVersionId: String(file.fileVersionId),
              name: file.name ?? "",
              weightage: file.weightage ?? null,
              version: file.version ?? 0,
            }),
          ),
        );
        GanttDetails.worktimes?.map((calendar: Worktime) => {
          if (selectedTask.calendar_id == calendar.calendarUniqueId) {
            setSelectedCalendarName(calendar.name);
          }
        });
        setIsCreatingActivity(false);
      } else {
        setFileAddEditData([]);
        setCurrentSelectedTask({
          ...currentSelectedTask,
          id: new ObjectId().toString(),
          isNewTask: true,
          // `TaskDisp` is the activity's display id. The server models it as `string | number`
          // (tasks.collection.ts:620) and a new activity seeds it with the running numeric counter,
          // but the web `GanttTask` declares it `string` only (types/scheduleDomain/types.ts:502).
          // Widening THAT declaration to match the server is the fix; until then the number cannot be
          // assigned without an assertion.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see comment above: blocked on GanttTask.TaskDisp being widened to `string | number`
          TaskDisp: taskCounter.current as number & string,
          progress: 0,
          type: "task",
          duration: 1,
        });
        setIsCreatingActivity(true);
      }
    }
  }, [showActivityInformationModal]);

  const getTaskTypes = useCallback(async () => {
    const response = await axiosWrapper.post<GetTaskTypesResponse>(`${getBackendUrl()}/schedule/getTaskTypes`, {
      scheduleId,
    });
    setTaskTypes(response.data.result ?? []);
  }, [scheduleId]);

  useEffect(() => {
    getTaskTypes();
  }, [getTaskTypes]);

  const handleSaveClick = async () => {
    setLoadingActivity(true);
    try {
      const selectedId = gantt.getSelectedId();
      const selectedTask = gantt.getTask(selectedId);

      if (currentSelectedTask.plannedStartDate && currentSelectedTask.plannedEndDate) {
        currentSelectedTask.plannedDuration = gantt.calculateDuration({
          start_date: new Date(currentSelectedTask.plannedStartDate),
          end_date: new Date(currentSelectedTask.plannedEndDate),
          task: selectedTask,
        });
      }

      if (!currentSelectedTask.start_date) {
        currentSelectedTask.start_date = new Date();
      }
      if (currentSelectedTask.end_date) {
        currentSelectedTask.duration = gantt.calculateDuration({
          start_date: new Date(currentSelectedTask.start_date),
          end_date: new Date(currentSelectedTask.end_date),
          task: selectedTask,
        });
      } else {
        currentSelectedTask.end_date = gantt.calculateEndDate({
          start_date: new Date(currentSelectedTask.start_date),
          duration: currentSelectedTask.duration ? currentSelectedTask.duration : 1,
          task: selectedTask,
        });
      }

      if (currentSelectedTask.isNewTask) {
        gantt.addTask({
          ...currentSelectedTask,
          text: currentSelectedTask.text || `New Activity ${taskCounter.current}`,
          duration: currentSelectedTask.duration || "1",
          // Both are filled in above (start_date defaulted to now, end_date computed from the
          // duration) before this runs, so there is nothing left to fall back to here.
          start_date: new Date(currentSelectedTask.start_date),
          end_date: new Date(currentSelectedTask.end_date),
          actualStartDate: currentSelectedTask.actualStartDate ? new Date(currentSelectedTask.actualStartDate) : null,
          actualEndDate: currentSelectedTask.actualEndDate ? new Date(currentSelectedTask.actualEndDate) : null,
          plannedStartDate: currentSelectedTask.plannedStartDate
            ? new Date(currentSelectedTask.plannedStartDate)
            : null,
          plannedEndDate: currentSelectedTask.plannedEndDate ? new Date(currentSelectedTask.plannedEndDate) : null,
          progress: currentSelectedTask.progress! / 100,
          calendar_id: currentSelectedTask.calendar_id,
          taskFiles: fileAddEditData.map((row) => ({
            fileVersionId: row.fileVersionId,
            weightage: row.weightage,
            name: row.name,
            version: row.version,
          })),
        });
      } else {
        // The resource endpoints take the activity's Mongo `_id` (`objectIdString`), and the vendored
        // gantt typings only promise a `string` — so it is parsed here instead of asserted. An
        // existing activity always carries one; without it the writes below cannot be addressed.
        const taskId = objectIdString.safeParse(selectedId).data;
        if (!taskId) {
          setLoadingActivity(false);
          dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
          return;
        }
        const tempTask: Task = gantt.getTask(currentSelectedTask.id!);
        for (const key of Object.keys(currentSelectedTask)) {
          if (
            key === "start_date" ||
            key === "end_date" ||
            key === "actualStartDate" ||
            key === "actualEndDate" ||
            key === "plannedStartDate" ||
            key === "plannedEndDate"
          ) {
            if (currentSelectedTask[key]) {
              currentSelectedTask[key] = new Date(currentSelectedTask[key]);
            }
          }
          tempTask[key] = currentSelectedTask[key];
        }
        tempTask.progress = (tempTask.progress || 0) / 100;
        if (deletedResources.length) {
          const response = await deleteResourceIds({
            taskId,
            scheduleId,
            // `deleteResourceIdsFromTaskRequest.owner[]` is a `.catchall(z.unknown())` object, so its
            // element type carries a string index signature — and `DeletedResource` is declared as an
            // `interface` (types/scheduleDomain/types.ts), which never gets TypeScript's implicit
            // index signature. Copying each row into an object literal satisfies that: the rows are
            // whole grid rows at runtime (Resource.tsx seeds them from `dataView.items`) and the
            // spread keeps every key, so the request body is unchanged.
            owner: deletedResources.map((resource) => ({ ...resource })),
          });
          if (response.data.status) {
            const updatedResourceData = selectedTask.owner.filter(
              (resource: DeletedResource) =>
                !deletedResources.some((deleted: DeletedResource) => deleted.resource_id === resource.resource_id),
            );
            selectedTask.owner = updatedResourceData;
            setDeletedResources([]);
          }
        }
        if (resourceAddEditData.length) {
          const saveUpdatedData = resourceAddEditData.map((resource: ResourceAddEditData) => {
            return {
              resource_id: resource.resource_id,
              counter: resource.counter,
              text: resource.text,
              // `owner[].availability` is `z.number().optional()`; a legacy string/"" would 400 the
              // whole save, so normalise here as well as on the way in.
              availability: toAvailability(resource.availability),
              type: resource.type,
              selectionSetIds: resource.selectionSetIds,
              queriesIds: resource.queriesIds,
            };
          });

          const response = await saveResourceIds({
            taskId,
            scheduleId,
            owner: saveUpdatedData,
          });
          if (response.data.status) {
            for (const updatedResource of resourceAddEditData) {
              let matched = false;

              for (const existedResource of selectedTask.owner ?? []) {
                if (updatedResource.resource_id === existedResource.resource_id) {
                  existedResource.availability = updatedResource.availability;
                  matched = true;
                }
              }

              if (!matched) {
                updatedResource.id = updatedResource.resource_id;
                selectedTask.owner.push(updatedResource);
              }
            }

            setResourceAddEditData([]);
          }
        }
        const deletedLinkIds = new Set(deletedLinks.map((link) => link.id.toString()));
        const liveBufferLinks = dependencyAddEditData.filter(
          (link: DependencyAddEditData) => !deletedLinkIds.has(link.id),
        );

        if (deletedLinks.length) {
          for (const link of deletedLinks) {
            const linkIdStr = link.id;
            if (gantt.isLinkExists(linkIdStr)) {
              gantt.deleteLink(linkIdStr);
            }
          }
          setDeletedLinks([]);
        }
        if (liveBufferLinks.length) {
          const saveUpdatedData = liveBufferLinks
            .map((link: DependencyAddEditData) => {
              const sourceId = link.linkType == "Predecessor" ? link.predId : selectedId;
              const targetId = link.linkType == "Predecessor" ? selectedId : link.successorId;

              if (!sourceId || !targetId) {
                return null;
              }

              return {
                id: link.id,
                source: sourceId,
                target: targetId,
                lag: link.lag || 0,
                type: link.type || "0",
              };
            })
            .filter((link): link is NonNullable<typeof link> => link !== null);

          for (const updatedLink of saveUpdatedData) {
            if (gantt.isLinkExists(updatedLink.id)) {
              const ganttLink = gantt.getLink(updatedLink.id);
              ganttLink.lag = updatedLink.lag;
              ganttLink.type = updatedLink.type;
              ganttLink.source = updatedLink.source;
              ganttLink.target = updatedLink.target;
              gantt.updateLink(updatedLink.id);
            } else if (gantt.isLinkAllowed(updatedLink)) {
              gantt.addLink(updatedLink);
            } else {
              dispatch(
                showToastNotification({
                  content: t("Invalid Link"),
                  type: TOAST_TYPE_ERROR,
                }),
              );
            }
          }
          setDependencyAddEditData([]);
        }
        if (filesChanged) {
          selectedTask.taskFiles = fileAddEditData.map((row) => ({
            fileVersionId: row.fileVersionId,
            weightage: row.weightage,
            name: row.name,
            version: row.version,
          }));
          setFilesChanged(false);
        }
        gantt.updateTask(currentSelectedTask.id!);
      }
    } catch (error) {
      setLoadingActivity(false);
      const errorMessage = readApiErrorMessage(error);
      dispatch(
        showToastNotification({
          content: errorMessage ? untranslated(errorMessage) : SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleTaskChanges = (field: string, value: string | number | Dayjs | Date | null) => {
    const selectedTask = gantt.getTask(currentSelectedTask.id!);
    const tempFields = { ...currentSelectedTask };
    tempFields[field] = value;
    if (ganttConfig.current == "hour" && field == "duration") {
      tempFields.duration = Math.ceil(tempFields.duration! * getHoursPerDay(selectedTask));
    }
    if (field == "duration") {
      if (!tempFields.start_date) tempFields.start_date = new Date();
      tempFields.end_date = gantt.calculateEndDate({
        start_date: tempFields.start_date,
        duration: tempFields.duration ? tempFields.duration : 1,
        task: selectedTask,
      });
    }
    if (field == "start_date" && tempFields.start_date && tempFields.end_date) {
      if (new Date(tempFields.start_date) >= new Date(tempFields.end_date)) {
        tempFields.end_date = gantt.calculateEndDate({
          start_date: tempFields.start_date,
          duration: tempFields.duration ? tempFields.duration : 1,
          task: selectedTask,
        });
      } else {
        tempFields.duration = gantt.calculateDuration({
          start_date: tempFields.start_date,
          end_date: tempFields.end_date,
          task: selectedTask,
        });
      }
    }
    if (field == "end_date" && tempFields.end_date) {
      if (!tempFields.start_date) tempFields.start_date = new Date();
      tempFields.duration = gantt.calculateDuration({
        start_date: tempFields.start_date,
        end_date: tempFields.end_date,
        task: selectedTask,
      });
    }
    setCurrentSelectedTask(tempFields);
  };
  const getDateComponent = (field: ActivityDateField) => {
    let value: Dayjs | null = null;
    if (currentSelectedTask[field]) {
      value = dayjs(currentSelectedTask[field]);
    } else if (currentSelectedTask[field] === null) {
      value = null;
    } else {
      if (field === "start_date") {
        value = dayjs();
      } else if (field === "end_date") {
        value = dayjs().add(1, "day");
      }
    }
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          timezone={userDetails.timeZone}
          {...(field === "end_date" ? { minDateTime: dayjs(currentSelectedTask.start_date ?? new Date()) } : {})}
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small",
              sx: {
                "& .MuiInputBase-root": {
                  height: "2rem",
                  fontSize: "1rem",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#666A84",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6BBBE3",
                  borderWidth: "0.0625rem",
                },
              },
            },
          }}
          format="MMM D YYYY, hh:mm A"
          value={value}
          onError={(reason) => {
            if (reason) {
              setDisableSave(true);
            } else {
              setDisableSave(false);
            }
          }}
          onChange={(newValue: Dayjs | null) => {
            if (newValue === null) {
              handleTaskChanges(field, null);
            } else if (newValue.isValid()) {
              handleTaskChanges(field, newValue.toDate());
            }
          }}
        />
      </LocalizationProvider>
    );
  };

  const taskModalContent = () => {
    // `getHoursPerDay` reads only the task's `calendar_id`, but its parameter is the vendored `Task`,
    // whose `id` is required — the modal's task is a `Partial`. It is handed over only once it has an
    // id (a task without one has no calendar either, so both paths resolve the global calendar).
    const taskForHoursPerDay: Task | undefined =
      currentSelectedTask.id === undefined ? undefined : { ...currentSelectedTask, id: currentSelectedTask.id };
    return (
      <div className="flex flex-col h-full overflow-auto">
        <div
          className="flex h-15"
          style={{
            borderBottomColor: currentTab == "General" ? "#ECF3FE" : "",
            borderBottomWidth: currentTab == "General" ? "1px" : "0",
          }}
        >
          <Box sx={{ width: "100%", height: "3.75rem", marginInlineStart: "2rem" }}>
            <Tabs
              value={currentTab}
              onChange={(event: React.SyntheticEvent, selectedTab: string) => setCurrentTab(selectedTab)}
              sx={{ "& .MuiTab-root": { fontSize: "1rem" } }}
            >
              <Tab label={t("General")} value="General" sx={{ marginInlineEnd: "2rem", marginTop: "0.5rem" }} />
              <Tab
                label={t("Predecessor")}
                value="Predecessor"
                disabled={currentSelectedTask.isNewTask}
                sx={{ marginInlineEnd: "2rem", marginTop: "0.5rem" }}
              />
              <Tab
                label={t("Successor")}
                value="Successor"
                disabled={currentSelectedTask.isNewTask}
                sx={{ marginInlineEnd: "2rem", marginTop: "0.5rem" }}
              />
              <Tab
                label={t("Resources")}
                value="Resources"
                disabled={currentSelectedTask.isNewTask}
                sx={{ marginInlineEnd: "2rem", marginTop: "0.5rem" }}
              />
              <Tab
                label={t("Files")}
                value="Files"
                disabled={currentSelectedTask.isNewTask}
                sx={{ marginInlineEnd: "2rem", marginTop: "0.5rem" }}
              />
            </Tabs>
          </Box>
        </div>
        {currentTab == "General" && (
          <div className="mt-6 w-full px-8">
            <div className="grid grid-cols-[9rem,1fr,8.125rem,1fr] gap-x-6 gap-y-6 items-center">
              <Text className="flex items-center h-4 text-base font-semibold">{t("ID")}</Text>
              <Text className="flex col-span-3">
                <TextFieldComponent
                  fullWidth
                  variant="outlined"
                  value={currentSelectedTask.TaskDisp}
                  onChange={(e) => {
                    handleTaskChanges("TaskDisp", e.target.value);
                  }}
                  placeholder={new Intl.NumberFormat(i18n.language).format(1)}
                  disabled
                />
              </Text>

              <Text className="flex items-center h-4 text-base font-semibold">
                {t("Name")}
                <Text className="text-red-500">*</Text>
              </Text>
              <Text className="w-full col-span-3">
                <TextFieldComponent
                  fullWidth
                  variant="outlined"
                  value={currentSelectedTask.text}
                  onChange={(e) => {
                    handleTaskChanges("text", e.target.value);
                  }}
                  defaultValue={`New Activity ${taskCounter.current}`}
                  placeholder={t("title")}
                  error={currentSelectedTask.text?.length! > maxCharacterLength}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {currentSelectedTask.text?.length ?? `New Activity ${taskCounter.current}`.length}/1000
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                {currentSelectedTask.text == "" && (
                  <Text className="text-red-500 text-xs">{t("This Field is required")}</Text>
                )}
              </Text>

              <Text className="flex items-center h-4 text-base font-semibold">
                {t("Start Date")}
                <Text className="text-red-500">*</Text>
              </Text>
              {getDateComponent("start_date")}

              <Text className="flex items-center h-4 text-base font-semibold">
                {t("End Date")}
                <Text className="text-red-500">*</Text>
              </Text>
              {getDateComponent("end_date")}

              <Text className="flex items-center h-4 text-base font-semibold">{t("Duration(In days)")} </Text>
              <TextFieldComponent
                type="number"
                size="small"
                value={
                  ganttConfig.current === "hour"
                    ? Math.ceil(
                        (currentSelectedTask.duration ?? 1) / getHoursPerDay(gantt.getTask(currentSelectedTask.id!)),
                      )
                    : currentSelectedTask.duration
                }
                onChange={(e) => {
                  handleTaskChanges("duration", Number(e.target.value));
                }}
                onBlur={() => {
                  if (currentSelectedTask.duration! < 1) {
                    setCurrentSelectedTask((prev: SelectedGanttTask) => ({
                      ...prev,
                      duration: 1,
                    }));
                  }
                }}
                defaultValue={1}
              />

              <Text className="flex items-center h-4 text-base font-semibold">{t("Activity Type")}</Text>
              <FormControl
                size="small"
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    height: "2rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#666A84",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6BBBE3",
                    borderWidth: "0.0625rem",
                  },
                }}
              >
                <Select
                  IconComponent={DownArrowIcon}
                  value={currentSelectedTask.type}
                  onChange={(e) => handleTaskChanges("type", e.target.value)}
                  defaultValue="task"
                  renderValue={(selected) => {
                    if (selected === "task") return t("Task Type");
                    if (selected === "project") return t("Project Type");
                    if (selected === "milestone") return t("Milestone Type");
                  }}
                  sx={{
                    "& .MuiSelect-icon": {
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "1rem",
                      height: "1rem",
                    },
                  }}
                  MenuProps={{
                    slotProps: {
                      paper: {
                        sx: {
                          "& .MuiMenuItem-root": { minHeight: "2rem" },
                        },
                      },
                      // MUI v9 folded Menu's `MenuListProps` into the `list` slot.
                      list: {
                        sx: {
                          py: 0,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="task" className="flex items-center gap-2 h-12 text-sm py-2">
                    <TaskIcon className="h-4 w-4" />
                    <Text>{t("Task Type")}</Text>
                  </MenuItem>
                  <MenuItem value="project" className="flex items-center gap-2 h-12 text-sm py-2">
                    <ProjectIcon className="h-4 w-4" />
                    <Text>{t("Project Type")}</Text>
                  </MenuItem>
                  <MenuItem value="milestone" className="flex items-center gap-2 h-12 text-sm py-2">
                    <MilestoneIcon className="h-4 w-4" />
                    <Text>{t("Milestone Type")}</Text>
                  </MenuItem>
                </Select>
              </FormControl>

              <Text className="flex items-center h-4 text-base font-semibold">{t("Calendar")}</Text>
              <FormControl
                size="small"
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    height: "2rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#666A84",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6BBBE3",
                    borderWidth: "0.0625rem",
                  },
                }}
              >
                <Select
                  value={selectedCalendarName || t("Based on Schedule’s Default Calender")}
                  renderValue={(value) => value}
                  IconComponent={DownArrowIcon}
                  sx={{
                    "& .MuiSelect-icon": {
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "1rem",
                      height: "1rem",
                    },
                  }}
                >
                  <MenuItemComponent
                    onClick={() => {
                      setShowCustomCalendars(false);
                      handleTaskChanges("calendar_id", null);
                      setSelectedCalendarName("");
                    }}
                  >
                    <Text>{t("Based on Schedule’s Default Calender")}</Text>
                  </MenuItemComponent>

                  <MenuItemComponent
                    ref={calendarItemRef}
                    value="Custom"
                    onMouseEnter={() => setShowCustomCalendars(true)}
                    onMouseLeave={() => setShowCustomCalendars(false)}
                    onClick={() => {
                      setShowCustomCalendars(!showCustomCalendars);
                    }}
                  >
                    <div className="w-full flex items-center justify-between gap-3">
                      <Text>{t("Custom")}</Text>
                      <IconComponent name="arrowRight" width="1rem" height="1rem" />
                    </div>
                  </MenuItemComponent>
                </Select>
                <PopperComponent
                  open={Boolean(showCustomCalendars)}
                  anchorEl={calendarItemRef.current}
                  onClose={() => setShowCustomCalendars(false)}
                  zIndex={1400}
                  onMouseEnter={() => setShowCustomCalendars(true)}
                >
                  {/* MenuList supplies the MenuListContext MUI v9's MenuItem requires (else it throws). */}
                  <MenuList disablePadding sx={{ outline: "none" }}>
                    {GanttDetails.worktimes?.map((calendar: Worktime) => {
                      return (
                        <MenuItemComponent
                          key={calendar._id}
                          onClick={() => {
                            setShowCustomCalendars(false);
                            handleTaskChanges("calendar_id", calendar.calendarUniqueId);
                            setSelectedCalendarName(calendar.name);
                          }}
                        >
                          <Text className="flex items-center justify-between gap-3">
                            <Text>{calendar.name}</Text>
                            {calendar.isDefault && (
                              <Chip
                                size="small"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  background: "#666a84",
                                  color: "white",
                                  marginInlineEnd: "1rem",
                                }}
                                label="Default"
                              />
                            )}
                          </Text>
                        </MenuItemComponent>
                      );
                    })}
                  </MenuList>
                </PopperComponent>
              </FormControl>

              <Text className="flex items-center h-4 text-base font-semibold">{t("Task Type")}</Text>
              <FormControl
                size="small"
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    height: "2rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#666A84",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6BBBE3",
                    borderWidth: "0.0625rem",
                  },
                }}
              >
                <Select
                  IconComponent={DownArrowIcon}
                  value={currentSelectedTask.taskTypeId}
                  onChange={(e) => handleTaskChanges("taskTypeId", e.target.value)}
                  sx={{
                    "& .MuiSelect-icon": {
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "1rem",
                      height: "1rem",
                    },
                  }}
                  MenuProps={{
                    slotProps: {
                      paper: {
                        sx: {
                          "& .MuiMenuItem-root": { minHeight: "2rem" },
                        },
                      },
                      // MUI v9 folded Menu's `MenuListProps` into the `list` slot.
                      list: {
                        sx: {
                          py: 0,
                        },
                      },
                    },
                  }}
                >
                  {taskTypes.map((item: TaskTypeRow) => (
                    <MenuItem key={item._id} value={item._id} className="flex items-center gap-2 h-12 text-sm py-2">
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!isCreatingActivity && (
                <Text className="flex items-center h-4 text-base font-semibold">{t("Planned Start Date")}</Text>
              )}
              {!isCreatingActivity && getDateComponent("plannedStartDate")}

              {!isCreatingActivity && (
                <Text className="flex items-center h-4 text-base font-semibold">{t("Planned End Date")}</Text>
              )}
              {!isCreatingActivity && getDateComponent("plannedEndDate")}
              <Text className="flex items-center h-4 text-base font-semibold text-primary_text">
                {currencySymbol ? `${t("Planned Value")} (${currencySymbol})` : t("Planned Value")}
              </Text>
              <TextFieldComponent
                type="number"
                size="small"
                onChange={(e) => {
                  handleTaskChanges("cost", Number(e.target.value));
                }}
                value={currentSelectedTask.cost || null}
              />
            </div>
          </div>
        )}

        {(currentTab === "Predecessor" || currentTab === "Successor") && (
          <Dependency
            currentTab={currentTab}
            gantt={gantt}
            currentSelectedTask={currentSelectedTask}
            deletedLinks={deletedLinks}
            setDeletedLinks={setDeletedLinks}
            dependencyAddEditData={dependencyAddEditData}
            setDependencyAddEditData={setDependencyAddEditData}
            hoursPerDay={getHoursPerDay(taskForHoursPerDay)}
          />
        )}

        {currentTab === "Resources" && (
          <Resource
            deletedResources={deletedResources}
            setDeletedResources={setDeletedResources}
            resourceAddEditData={resourceAddEditData}
            setResourceAddEditData={setResourceAddEditData}
          />
        )}

        {currentTab === "Files" && (
          <ActivityFiles
            spaceId={props.spaceId}
            projectId={props.projectId}
            fileAddEditData={fileAddEditData}
            setFileAddEditData={setFileAddEditData}
            setFilesChanged={setFilesChanged}
          />
        )}
      </div>
    );
  };

  const taskFooterContent = () => {
    // Clearing a date in the picker calls `handleTaskChanges(field, null)`, which writes `null`
    // through `GanttTask`'s index signature — so these two comparisons are live at runtime even
    // though `GanttTask` declares both fields `Date | undefined`
    // (types/scheduleDomain/types.ts:495-496). Widening THOSE to `Date | null` is the fix.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- see comment above: the picker writes null through the index signature
    const hasClearedDate = currentSelectedTask.start_date === null || currentSelectedTask.end_date === null;
    return (
      <div className="flex px-8 py-4 gap-4" style={{ borderTopColor: "#ECF3FE", borderTopWidth: "0.0625rem" }}>
        <ButtonComponent
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={handleSaveClick}
          loading={loadingActivity}
          disabled={
            currentSelectedTask.text == "" ||
            currentSelectedTask.text?.length! > maxCharacterLength ||
            hasClearedDate ||
            disableSave ||
            loadingActivity
          }
        >
          <Text>{t("Confirm")}</Text>
        </ButtonComponent>
        <ButtonComponent
          buttonType={BUTTON_TYPES.BORDER_BUTTON}
          onClick={() => {
            setShowActivityInformationModal(false);
            setCurrentTab("General");
            setResourceAddEditData([]);
            if (!gantt.getSelectedId()) {
              setCurrentSelectedTask({});
            }
          }}
        >
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  };

  return (
    <div>
      <ModalStateDialogComponent
        modalPadding={0}
        headerTitle={
          <Text className="h-16 w-full justify-center px-8 py-5 font-bold text-2xl">
            {isCreatingActivity ? t("Create Activity") : t("Edit Activity")}
          </Text>
        }
        onCancel={() => {
          setShowActivityInformationModal(false);
          setCurrentTab("General");
          if (!gantt.getSelectedId()) {
            setCurrentSelectedTask({});
          }
        }}
        modelContent={taskModalContent()}
        footerContent={taskFooterContent()}
        isVisible={showActivityInformationModal}
        width="80%"
        height="80%"
      />
    </div>
  );
}
export default ActivityInformationModal;
